import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import type { LeaderboardEntry } from "../../models/types";
import {
  firstNameOf,
  formatDurationMs,
  LEADERBOARD_MODES,
  leaderboardPoints,
  modeTitle,
} from "../../utils/calendar";
import { IconTrophy } from "../shared/Icons";

type Scope = "overall" | "byGame";

interface OverallEntry {
  rank: number;
  personId: string;
  displayName: string;
  totalPoints: number;
  averagePercent: number;
  gamesPlayed: number;
  totalGames: number;
  topModes: string;
}

function entryPoints(entry: LeaderboardEntry): number {
  return leaderboardPoints(entry.percent, entry.duration_ms);
}

function buildOverall(
  byMode: Record<string, LeaderboardEntry[]>,
): OverallEntry[] {
  const totalGames = LEADERBOARD_MODES.length;
  const byPerson = new Map<
    string,
    { displayName: string; points: number; percents: number[]; modes: { title: string; points: number }[] }
  >();

  for (const mode of LEADERBOARD_MODES) {
    const ranked = [...(byMode[mode] ?? [])].sort((a, b) => {
      const pa = entryPoints(a);
      const pb = entryPoints(b);
      if (pa !== pb) return pb - pa;
      return a.duration_ms - b.duration_ms;
    });
    for (const entry of ranked) {
      const pts = entryPoints(entry);
      const existing = byPerson.get(entry.person_id);
      if (existing) {
        existing.points += pts;
        existing.percents.push(entry.percent);
        existing.modes.push({ title: modeTitle(mode), points: pts });
      } else {
        byPerson.set(entry.person_id, {
          displayName: entry.display_name,
          points: pts,
          percents: [entry.percent],
          modes: [{ title: modeTitle(mode), points: pts }],
        });
      }
    }
  }

  const rows = [...byPerson.entries()]
    .map(([personId, data]) => {
      const avg =
        data.percents.length > 0
          ? data.percents.reduce((a, b) => a + b, 0) / data.percents.length
          : 0;
      const topModes = [...data.modes]
        .sort((a, b) => b.points - a.points)
        .slice(0, 2)
        .map((m) => m.title)
        .join(", ");
      return {
        personId,
        displayName: data.displayName,
        totalPoints: data.points,
        averagePercent: avg,
        gamesPlayed: data.percents.length,
        totalGames,
        topModes,
      };
    })
    .sort((a, b) => {
      if (a.totalPoints !== b.totalPoints) return b.totalPoints - a.totalPoints;
      return a.displayName.localeCompare(b.displayName);
    });

  return rows.map((row, i) => ({ ...row, rank: i + 1 }));
}

export function LeaderboardPanel({
  initialMode,
  onDone,
}: {
  initialMode?: string;
  onDone?: () => void;
}) {
  const { refreshLeaderboard, leaderboardByMode, session } = useApp();
  const [scope, setScope] = useState<Scope>("overall");
  const [mode, setMode] = useState(initialMode ?? "easy");
  const [syncing, setSyncing] = useState(false);
  const me = session?.person_id;

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      setSyncing(true);
      try {
        await Promise.all(LEADERBOARD_MODES.map((m) => refreshLeaderboard(m)));
      } finally {
        if (!cancelled) setSyncing(false);
      }
    };
    void sync();
    return () => {
      cancelled = true;
    };
  }, [refreshLeaderboard]);

  useEffect(() => {
    if (initialMode) {
      setMode(initialMode);
      setScope("byGame");
    }
  }, [initialMode]);

  const overall = useMemo(
    () => buildOverall(leaderboardByMode),
    [leaderboardByMode],
  );

  const ranked = useMemo(() => {
    const entries = [...(leaderboardByMode[mode] ?? [])];
    return entries
      .map((e) => ({ ...e, points: entryPoints(e) }))
      .sort((a, b) => {
        if (a.points !== b.points) return b.points - a.points;
        return a.duration_ms - b.duration_ms;
      })
      .map((e, i) => ({ ...e, rank: i + 1 }));
  }, [leaderboardByMode, mode]);

  const yourOverall = overall.find((e) => e.personId === me);
  const yourGame = ranked.find((e) => e.person_id === me);
  const isEmpty = scope === "overall" ? overall.length === 0 : ranked.length === 0;
  const podiumSource =
    scope === "overall"
      ? overall.slice(0, 3).map((e) => ({
          id: e.personId,
          name: e.displayName,
          points: e.totalPoints,
          detail: `${e.gamesPlayed} games`,
          isYou: e.personId === me,
        }))
      : ranked.slice(0, 3).map((e) => ({
          id: e.person_id,
          name: e.display_name,
          points: e.points,
          detail: formatDurationMs(e.duration_ms),
          isYou: e.person_id === me,
        }));

  return (
    <div className="leaderboard-panel stack">
      <div className="leaderboard-hero">
        <div className="leaderboard-hero-title">
          <IconTrophy />
          <h2 className="title-serif" style={{ margin: 0 }}>
            Family Showdown
          </h2>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          {scope === "overall"
            ? "Total points across every board — accuracy earns up to 100 per game, perfect runs add a speed bonus, and playing more games raises your total."
            : "Pick any board — flashcards, birth order, Family Match, Family Lore, or Who's With Who. Points come from accuracy (up to 100) plus a speed bonus on perfect runs."}
        </p>
      </div>

      {onDone ? (
        <button type="button" className="btn-ghost" style={{ alignSelf: "stretch" }} onClick={onDone}>
          Done
        </button>
      ) : null}

      <div className="scope-picker">
        {(["overall", "byGame"] as const).map((item) => (
          <button
            key={item}
            type="button"
            className={scope === item ? "active" : ""}
            onClick={() => setScope(item)}
          >
            {item === "overall" ? "Overall" : "By game"}
          </button>
        ))}
      </div>

      {scope === "byGame" ? (
        <div className="board-chips">
          <div className="section-label" style={{ marginBottom: 4 }}>
            Board
          </div>
          <div className="board-chips-row">
            {LEADERBOARD_MODES.map((m) => (
              <button
                key={m}
                type="button"
                className={mode === m ? "active" : ""}
                onClick={() => {
                  setMode(m);
                  void refreshLeaderboard(m);
                }}
              >
                {modeTitle(m)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {scope === "overall" && yourOverall ? (
        <div className="your-banner">
          <div className="your-rank-circle">#{yourOverall.rank}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong>Your standing · {yourOverall.displayName}</strong>
            <div className="muted" style={{ fontSize: "0.85rem" }}>
              {yourOverall.totalPoints} pts · {yourOverall.gamesPlayed}/{yourOverall.totalGames}{" "}
              games · {Math.round(yourOverall.averagePercent)}% avg
            </div>
          </div>
          <div className="your-pts">{yourOverall.totalPoints}</div>
        </div>
      ) : null}

      {scope === "byGame" && yourGame ? (
        <div className="your-banner">
          <div className="your-rank-circle">#{yourGame.rank}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong>Your best · {yourGame.display_name}</strong>
            <div className="muted" style={{ fontSize: "0.85rem" }}>
              {Math.round(yourGame.percent)}% · {formatDurationMs(yourGame.duration_ms)} ·{" "}
              {yourGame.score}/{yourGame.total} correct
            </div>
          </div>
          <div className="your-pts">{yourGame.points}</div>
        </div>
      ) : null}

      <p className="muted" style={{ fontSize: "0.8rem", margin: 0 }}>
        {syncing ? "Syncing with family server…" : "Family scores synced"}
      </p>

      {isEmpty ? (
        <div className="leaderboard-empty">
          <IconTrophy />
          <h3 className="title-serif">No scores yet</h3>
          <p className="muted">
            {scope === "overall"
              ? "Finish any flashcard or birth-order game to get on the family board."
              : `Finish a ${modeTitle(mode)} round to take #1.`}
          </p>
        </div>
      ) : (
        <>
          {podiumSource.length > 0 ? (
            <div className="podium">
              {[1, 0, 2].map((slot) => {
                const entry = podiumSource[slot];
                if (!entry) return <div key={slot} className="podium-slot empty" />;
                const heights = [108, 88, 72];
                const medals = ["🥇", "🥈", "🥉"];
                const orderIndex = slot; // 0=gold, 1=silver, 2=bronze
                return (
                  <div key={entry.id} className="podium-slot">
                    <span className="podium-medal">{medals[orderIndex]}</span>
                    <strong>{firstNameOf(entry.name)}</strong>
                    <span className="podium-pts">{entry.points} pts</span>
                    <div
                      className={`podium-bar${entry.isYou ? " you" : ""}`}
                      style={{ height: heights[orderIndex] }}
                    >
                      <span>{entry.detail}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className="section-label">Full standings</div>
          <div className="stack">
            {scope === "overall"
              ? overall.map((entry) => {
                  const isYou = entry.personId === me;
                  return (
                    <div
                      key={entry.personId}
                      className={`leaderboard-row${isYou ? " is-you" : ""}`}
                    >
                      <div className={`rank-badge${entry.rank <= 3 ? " top" : ""}`}>
                        #{entry.rank}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong>
                          {entry.displayName}
                          {isYou ? <span className="you-pill">YOU</span> : null}
                        </strong>
                        <div className="muted" style={{ fontSize: "0.8rem" }}>
                          {entry.gamesPlayed}/{entry.totalGames} games ·{" "}
                          {Math.round(entry.averagePercent)}% avg
                          {entry.topModes ? ` · best: ${entry.topModes}` : ""}
                        </div>
                      </div>
                      <div className={`row-pts${isYou ? " you" : ""}`}>
                        {entry.totalPoints} pts
                      </div>
                    </div>
                  );
                })
              : ranked.map((entry) => {
                  const isYou = entry.person_id === me;
                  return (
                    <div
                      key={`${entry.person_id}-${entry.rank}`}
                      className={`leaderboard-row${isYou ? " is-you" : ""}`}
                    >
                      <div className={`rank-badge${entry.rank <= 3 ? " top" : ""}`}>
                        #{entry.rank}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong>
                          {entry.display_name}
                          {isYou ? <span className="you-pill">YOU</span> : null}
                        </strong>
                        <div className="muted" style={{ fontSize: "0.8rem" }}>
                          {Math.round(entry.percent)}% · {formatDurationMs(entry.duration_ms)} ·{" "}
                          {entry.score}/{entry.total} correct
                        </div>
                      </div>
                      <div className={`row-pts${isYou ? " you" : ""}`}>
                        {entry.points} pts
                      </div>
                    </div>
                  );
                })}
          </div>
        </>
      )}
    </div>
  );
}
